import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {
  GoogleMap,
  DirectionsRenderer,
  useJsApiLoader,
  OverlayView,
  Polyline,
  Marker,
} from '@react-google-maps/api'
import {KTSVG, toAbsoluteUrl} from '../../../../../_gori/helpers'
import {
  AirportLocation,
  MessageItemCommonProps,
  MessageTypeShipmentStatusContent,
  ShippingAddress,
} from '../../definitions'
import clsx from 'clsx'
import {useIntl} from 'react-intl'
import {
  convertAddressToString,
  getAirportLocation,
  getA1LabelFromStatus,
  getLatLngFromAddress,
} from '../../utils'
import {compact, isEmpty, last, mean, startCase, uniq} from 'lodash'
import {useChatbotContext} from '../../context'
import {CHATBOT_MODE} from '../../definitions/type'
import {getCSSVariableValue} from '../../../../../_gori/assets/ts/_utils'

const COUNT_STEPS = 4

const polylineOptions = {
  strokeColor: '#1890ff',
  strokeOpacity: 1.0,
  strokeWeight: 4,
  geodesic: false,
}

type MessageItemTrackingShipmentProps = MessageItemCommonProps<MessageTypeShipmentStatusContent>

const PackageMarker = ({lat = 0, lng = 0}) => {
  return (
    <OverlayView position={{lat, lng}} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
      <div className='d-flex justify-content-center align-items-center shadow-lg rounded-circle bg-white w-36px h-36px'>
        <img
          src={toAbsoluteUrl('/media/gori/chatbot/shipping-order-marker.svg')}
          alt='current package'
          width={30}
          height={30}
        />
      </div>
    </OverlayView>
  )
}

const STATUS_LABEL = {
  unknown: {
    label: 'UNKNOWN',
    step: 0,
  },
  failed: {
    label: 'FAILED',
    step: 0,
  },
  voided: {
    label: 'VOIDED',
    step: 0,
  },
  preparing: {
    label: 'PREPARING',
    step: 0,
  },
  pre_transit: {
    label: 'PRE_TRANSIT',
    step: 1,
  },
  in_transit: {
    label: 'IN_TRANSIT',
    step: 2,
  },
  out_for_delivery: {
    label: 'OUT_FOR_DELIVERY',
    step: 3,
  },
  delivered: {
    label: 'DELIVERED',
    step: 4,
  },
}
export const MessageItemTrackingShipment = ({message}: MessageItemTrackingShipmentProps) => {
  const intl = useIntl()
  const {mode} = useChatbotContext()

  const {isLoaded} = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: ['places'],
    language: intl.locale,
  })
  const mapRef = useRef<google.maps.Map | null>(null)
  const [shipFromLatLng, setShipFromLatLng] = useState<google.maps.LatLngLiteral>({lat: 0, lng: 0})
  const [shipToLatLng, setShipToLatLng] = useState<google.maps.LatLngLiteral>({lat: 0, lng: 0})
  const [packageLocation, setPackageLocation] = useState<google.maps.LatLngLiteral | null>(null)

  const {ship_from, ship_to, waypoints, status} = message.content

  const a1FlightInfo = message.content.a1_flight_info

  const isViewA1Map = useMemo(() => {
    return !isEmpty(a1FlightInfo) && a1FlightInfo?.status !== 'transferred'
  }, [a1FlightInfo])
  const activeStatus = useMemo(() => {
    if (isViewA1Map) {
      return {
        label: getA1LabelFromStatus(a1FlightInfo?.status || ''),
        step: 1,
      }
    }
    return STATUS_LABEL[status] || STATUS_LABEL['out_for_delivery']
  }, [a1FlightInfo?.status, isViewA1Map, status])

  const isDelay = useMemo(() => !isEmpty(message.content.delay), [message.content.delay])

  const isDelivered = useMemo(() => status === 'delivered', [status])

  const primary100Color = getCSSVariableValue('--bs-primary-100')
  const warningColor = getCSSVariableValue('--bs-warning')

  const statusLabel = activeStatus.label ? intl.formatMessage({id: activeStatus.label}) : ''
  const [selectedMarkerIndex, setSelectedMarkerIndex] = useState<number | null>(null)

  const containerStyle = useMemo(() => {
    return {
      width: '100%',
      height: mode === CHATBOT_MODE.DEFAULT ? '270px' : '330px',
      borderRadius: '12px',
    }
  }, [mode])

  const [a1AirportLocations, setA1AirportLocations] = useState<{
    from: AirportLocation
    to: AirportLocation
    layover: AirportLocation[]
    latestFlight: AirportLocation | null
  } | null>(null)
  const [directions, setDirections] = React.useState<google.maps.DirectionsResult | null>(null)

  const convertA1FlightInfo = useCallback(async () => {
    if (!a1FlightInfo) return
    const codes = uniq([
      a1FlightInfo.from,
      a1FlightInfo.to,
      a1FlightInfo.latest_flight,
      ...(a1FlightInfo.layover || []),
    ])
    Promise.all(codes.map((code) => getAirportLocation(code).then((loc) => ({code, loc})))).then(
      (results) => {
        const locationMap: Record<string, AirportLocation> = {}
        results.forEach(({code, loc}) => {
          if (loc) {
            locationMap[code] = {...loc, code}
          }
        })

        if (results.length && mapRef.current) {
          const bounds = new window.google.maps.LatLngBounds()

          Object.values(locationMap).forEach((location) => {
            bounds.extend(new window.google.maps.LatLng(location?.lat || 0, location?.lng || 0))
          })

          mapRef.current.fitBounds(bounds)
        }

        setA1AirportLocations({
          from: locationMap[a1FlightInfo.from] || null,
          to: locationMap[a1FlightInfo.to] || null,
          layover: a1FlightInfo?.layover?.map((code) => locationMap[code] || null) || [],
          latestFlight: locationMap[a1FlightInfo.latest_flight] || null,
        })
      }
    )
  }, [a1FlightInfo])

  const a1FlightPath: AirportLocation[] = useMemo(() => {
    return compact([
      a1AirportLocations?.from,
      ...(a1AirportLocations?.layover || []),
      a1AirportLocations?.to,
    ])
  }, [a1AirportLocations?.from, a1AirportLocations?.layover, a1AirportLocations?.to])

  const convertAddressToLatLng = useCallback(async () => {
    const lastWaypoint: ShippingAddress = last(waypoints) || {}
    let requests = [getLatLngFromAddress(ship_from), getLatLngFromAddress(ship_to)]
    if (!isDelivered && lastWaypoint) {
      requests.push(getLatLngFromAddress(lastWaypoint))
    }

    const data = await Promise.all(requests)

    if (data[0]) {
      setShipFromLatLng(data[0])
    }
    if (data[1]) {
      setShipToLatLng(data[1])
    }
    setPackageLocation(last(data) || null)
  }, [isDelivered, ship_from, ship_to, waypoints])

  useEffect(() => {
    if (!isLoaded) return
    if (isViewA1Map) {
      convertA1FlightInfo()
    } else {
      convertAddressToLatLng()
    }
  }, [convertA1FlightInfo, convertAddressToLatLng, isLoaded, isViewA1Map])

  const clearSelectedMarker = useCallback(() => {
    setSelectedMarkerIndex(null)
  }, [])

  const center = useMemo(() => {
    if (isViewA1Map) {
      const lat = mean([a1AirportLocations?.from?.lat, a1AirportLocations?.latestFlight?.lat])
      const lng = mean([a1AirportLocations?.from?.lng, a1AirportLocations?.latestFlight?.lng])

      if (lat && lng) return {lat, lng}
    } else {
      const lat = mean([shipFromLatLng.lat, shipToLatLng.lat])
      const lng = mean([shipFromLatLng.lng, shipToLatLng.lng])

      if (lat && lng) return {lat, lng}
    }
  }, [
    a1AirportLocations?.from?.lat,
    a1AirportLocations?.from?.lng,
    a1AirportLocations?.latestFlight?.lat,
    a1AirportLocations?.latestFlight?.lng,
    isViewA1Map,
    shipFromLatLng.lat,
    shipFromLatLng.lng,
    shipToLatLng.lat,
    shipToLatLng.lng,
  ])

  const shipFromString = convertAddressToString(ship_from)
  const shipToString = convertAddressToString(ship_to)

  const onLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map

      if (!isViewA1Map) {
        const directionsService = new google.maps.DirectionsService()
        const locations = uniq(
          waypoints.filter((item) => item.state && item.zip).map((wp) => convertAddressToString(wp))
        ).map((location) => ({location, stopover: true})) as google.maps.DirectionsWaypoint[]
        directionsService.route(
          {
            origin: shipFromString,
            destination: shipToString,
            travelMode: google.maps.TravelMode.DRIVING,
            waypoints: locations,
          },
          (result, status) => {
            if (status === google.maps.DirectionsStatus.OK) {
              setDirections(result)
            } else {
              console.error('Directions request failed:', status)
            }
          }
        )
      }
    },
    [shipFromString, shipToString, waypoints, isViewA1Map]
  )

  const currentA1FlightPathRouteIndex = useMemo(() => {
    return a1FlightPath.findIndex((point) => point?.code === a1FlightInfo?.latest_flight)
  }, [a1FlightInfo?.latest_flight, a1FlightPath])

  if (!isLoaded)
    return <div className='placeholder-lg rounded-2 bg-secondary rounded-4 h-270px'></div>

  return (
    <div className='p-4 rounded-3 bg-primary-200 d-flex flex-column gap-2'>
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={center}
        zoom={10}
        onLoad={onLoad}
        options={{
          mapTypeControl: false,
          streetViewControl: false,
        }}
        onClick={() => clearSelectedMarker()}
        onZoomChanged={() => clearSelectedMarker()}
      >
        {isViewA1Map ? (
          <>
            <Polyline
              path={
                a1FlightPath.slice(
                  0,
                  currentA1FlightPathRouteIndex + 1
                ) as google.maps.LatLngLiteral[]
              }
              options={{...polylineOptions, strokeColor: primary100Color}}
            />
            <Polyline
              path={
                a1FlightPath.slice(currentA1FlightPathRouteIndex) as google.maps.LatLngLiteral[]
              }
              options={{...polylineOptions, strokeColor: warningColor}}
            />
            {a1FlightPath.map((point, index) => {
              if (point?.code === a1FlightInfo?.latest_flight) {
                return <PackageMarker lat={point?.lat} lng={point?.lng} key={index} />
              }
              return (
                <Marker
                  key={index}
                  onClick={() => setSelectedMarkerIndex(index)}
                  position={{lat: point?.lat || 0, lng: point?.lng || 0}}
                  label={
                    selectedMarkerIndex === index
                      ? {
                          text: point?.name || '',
                          className:
                            'text-dark bg-white shadow-lg px-3 py-2 rounded-3 fw-bold fs-7',
                        }
                      : undefined
                  }
                />
              )
            })}
          </>
        ) : (
          <>
            {packageLocation && (
              <PackageMarker lat={packageLocation.lat} lng={packageLocation.lng} />
            )}

            {directions && (
              <DirectionsRenderer
                directions={directions}
                options={
                  {
                    // markerOptions: {
                    //   icon: {
                    //     url: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSVEhkNLLP_d6x2jUaGmJic3piqrPhaua5mpQ&s',
                    //     scaledSize: new google.maps.Size(30, 30),
                    //   }
                    // },
                  }
                }
              />
            )}
          </>
        )}
      </GoogleMap>

      <div className='rounded-3 bg-white p-4'>
        {isDelay && (
          <div className='py-3 border-1 border border-danger rounded-2 mb-5 px-6'>
            <div className='d-flex gap-2 align-items-center justify-content-center mb-1'>
              <KTSVG path='/media/gori/chatbot/warning.svg' className='svg svg-icon-1' />
              <span className='text-danger fw-bolder fs-6'>
                {startCase(intl.formatMessage({id: 'SERVER_WEATHER_EVENT'}))}
              </span>
            </div>
            <div className='text-center'>
              <span className='mt-2'>{message.content.delay?.delay_reason}</span>
            </div>
          </div>
        )}
        {/* TODO: [N] */}
        {/* <div className='fs-5 fw-bolder text-center mb-2'>
          {isDelivered
            ? intl.formatMessage({id: 'DELIVERED'})
            : intl.formatMessage({id: 'EST_DELIVERY'})}
          : <span className='text-primary-200'>{deliveredDate}</span>
        </div> */}
        {isDelay && (
          <div className='text-danger mb-2 fw-bolder fs-5 text-center'>
            {intl.formatMessage({id: 'EST_DELAY'})}: {message.content.delay?.delay_time}
          </div>
        )}
        <div
          className={clsx({
            'mx-auto align-self-center w-75': mode === CHATBOT_MODE.LARGE,
          })}
        >
          <div className='d-flex'>
            <span className='text-gray-700 w-100px text-left flex-shrink-0'>
              {intl.formatMessage({id: 'DELIVERY_TO'})}{' '}
            </span>
            <span className='fw-bolder text-break'>{convertAddressToString(ship_to)}</span>
          </div>
          {!!message.content?.a1_flight_info?.from && (
            <div className='d-flex'>
              <span className='text-gray-700 w-100px text-left flex-shrink-0'>
                {intl.formatMessage({id: 'SHIPMENT_FROM'})}{' '}
              </span>
              <span className='fw-bolder text-break'>{message.content.a1_flight_info?.from}</span>
            </div>
          )}
          {!!message.content?.a1_flight_info?.to && (
            <div className='d-flex'>
              <span className='text-gray-700 w-100px text-left flex-shrink-0'>
                {intl.formatMessage({id: 'SHIPMENT_TO'})}{' '}
              </span>
              <span className='fw-bolder text-break'>{message.content.a1_flight_info?.to}</span>
            </div>
          )}
          <div className='d-flex'>
            <span className='text-gray-700 w-100px text-left flex-shrink-0'>
              {intl.formatMessage({id: 'STATUS'})}{' '}
            </span>
            <span className='fw-bolder text-break'>{statusLabel}</span>
          </div>
        </div>
        <div className='mt-6 p-0'>
          <div className='row gap-2 px-4'>
            {new Array(COUNT_STEPS).fill('').map((_, index) => (
              <div
                className={clsx('col h-4px w-100 rounded-pill', {
                  'bg-primary-200': index <= activeStatus.step,
                  'bg-gray-400': index > activeStatus.step,
                })}
                key={index}
              ></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
